import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Popover,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useLocation } from 'react-router-dom';

import { TrackedIconButton as IconButtonTracked } from '../../components/TrackedComponents';
import useApi from '../../hooks/api';
import useProfile, { type ExtendedProfile } from '../../hooks/profile';
import { OE_ROLES } from '../content/Users/UsersComponent';
import { HelpChatMessage, postDashboardHelpChat } from './dashboardHelpChatApi';
import { captureHelpChatViewportScreenshotJpegBase64 } from './dashboardHelpChatScreenshot';

function isAbortError(err: unknown): boolean {
    return err instanceof DOMException
        ? err.name === 'AbortError'
        : err instanceof Error && err.name === 'AbortError';
}

export interface DashboardHelpChatDialogProps {
    /** When non-null, the balloon is shown anchored to this element. */
    anchorEl: HTMLElement | null;
    onClose: () => void;
}

const DashboardHelpChatDialog: React.FC<DashboardHelpChatDialogProps> = ({ anchorEl, onClose }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { profile } = useProfile() as {
        profile: ExtendedProfile;
        setProfile: CallableFunction;
        resetProfile: CallableFunction;
    };
    const { token } = useApi();
    const location = useLocation();
    const [thread, setThread] = useState<HelpChatMessage[]>([]);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [attachScreenshot, setAttachScreenshot] = useState(true);
    const scrollAnchorRef = useRef<HTMLDivElement | null>(null);

    const open = Boolean(anchorEl);
    const isRtl = theme.direction === 'rtl';
    /** At least one assistant reply received — expand layout for ongoing conversation. */
    const chatActive = thread.some((m) => m.role === 'assistant');
    const expandedHeight = 'calc(100vh * 2 / 3)';

    useEffect(() => {
        if (anchorEl) {
            setDraft('');
            setError(null);
            setBusy(false);
        }
    }, [anchorEl]);

    useEffect(() => {
        const el = scrollAnchorRef.current;
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    }, [thread, busy, anchorEl]);

    const send = useCallback(async () => {
        const text = draft.trim();
        if (!text || busy) {
            return;
        }
        if (!token?.trim()) {
            setError(t('navigator.help_chat_sign_in_required'));
            return;
        }
        const prevThread = thread;
        const nextThread: HelpChatMessage[] = [...prevThread, { role: 'user', content: text }];
        setThread(nextThread);
        setDraft('');
        setError(null);
        setBusy(true);
        try {
            let pageScreenshotJpegBase64: string | null = null;
            if (attachScreenshot) {
                try {
                    pageScreenshotJpegBase64 = await captureHelpChatViewportScreenshotJpegBase64();
                } catch {
                    setError(t('navigator.help_chat_screenshot_failed'));
                    setThread(prevThread);
                    setDraft(text);
                    setBusy(false);
                    return;
                }
            }
            const data = await postDashboardHelpChat(nextThread, {
                profile,
                pathnameWithSearch: `${location.pathname}${location.search}`,
                accessToken: token,
                pageScreenshotJpegBase64,
            });
            if (data.error) {
                setError(data.error);
                setThread(prevThread);
                return;
            }
            if (data.reply !== undefined) {
                setThread([...nextThread, { role: 'assistant', content: data.reply }]);
            }
        } catch (err) {
            setError(
                isAbortError(err) ? t('navigator.help_chat_timeout') : t('navigator.help_chat_network_error'),
            );
            setThread(prevThread);
        } finally {
            setBusy(false);
        }
    }, [attachScreenshot, busy, draft, thread, t, profile, token, location.pathname, location.search]);

    if (!OE_ROLES.includes(profile.role)) {
        return null;
    }

    return (
        <Popover
            id="dashboard-help-chat"
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            disableAutoFocus
            disableRestoreFocus
            TransitionProps={{ timeout: 0 }}
            hideBackdrop
            disableScrollLock
            anchorOrigin={
                isRtl ? { vertical: 'center', horizontal: 'left' } : { vertical: 'center', horizontal: 'right' }
            }
            transformOrigin={
                isRtl ? { vertical: 'center', horizontal: 'right' } : { vertical: 'center', horizontal: 'left' }
            }
            PaperProps={{
                ...{ 'data-testid': 'help-chat-balloon' },
                elevation: 8,
                role: 'dialog',
                'aria-labelledby': 'dashboard-help-chat-title',
                sx: {
                    ...(chatActive
                        ? {
                              width: 'min(50vw, calc(100vw - 24px))',
                              maxWidth: 'min(50vw, calc(100vw - 24px))',
                          }
                        : {
                              width: 400,
                              maxWidth: 'min(400px, calc(100vw - 24px))',
                          }),
                    zIndex: (t) => t.zIndex.modal + 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: chatActive ? expandedHeight : 'min(70vh, 520px)',
                    ...(chatActive ? { height: expandedHeight, minHeight: 0 } : {}),
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Typography id="dashboard-help-chat-title" variant="subtitle1" component="h2">
                        {t('navigator.help_chat_heading')}
                    </Typography>
                    <IconButtonTracked
                        id="dashboard-help-chat-close"
                        buttonType="navigator"
                        size="small"
                        aria-label={t('table.close')}
                        onClick={onClose}
                        data-testid="help-chat-close"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButtonTracked>
                </Box>
                <Box
                    sx={{
                        px: 2,
                        pt: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        flex: 1,
                        minHeight: chatActive ? 0 : 200,
                        overflow: 'hidden',
                    }}
                >
                    {thread.length === 0 && (
                        <>
                            <Typography variant="body2" color="text.secondary" data-testid="help-chat-welcome">
                                {t('navigator.help_chat_welcome')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t('navigator.help_chat_hint')}
                            </Typography>
                        </>
                    )}
                    {error ? (
                        <Alert severity="error" data-testid="help-chat-error">
                            {error}
                        </Alert>
                    ) : null}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            py: 1,
                            minHeight: 120,
                        }}
                    >
                        {thread.map((m, i) => (
                            <Box
                                key={i}
                                data-testid={`help-chat-msg-${m.role}-${i}`}
                                sx={{
                                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                                    maxWidth: '92%',
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 1,
                                    bgcolor: m.role === 'user' ? 'primary.light' : 'action.hover',
                                    color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                                }}
                            >
                                {m.role === 'assistant' ? (
                                    <Box className="help-chat-md" sx={{ '& a': { wordBreak: 'break-all' } }}>
                                        <ReactMarkdown
                                            components={{
                                                a: ({ ...props }) => (
                                                    <a {...props} target="_blank" rel="noopener noreferrer" />
                                                ),
                                            }}
                                        >
                                            {m.content}
                                        </ReactMarkdown>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {m.content}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                        <div ref={scrollAnchorRef} />
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        borderTop: 1,
                        borderColor: 'divider',
                    }}
                >
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        size="small"
                        value={draft}
                        disabled={busy}
                        placeholder={t('navigator.help_chat_placeholder')}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                void send();
                            }
                        }}
                        inputProps={{ 'data-testid': 'help-chat-input' }}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                        }}
                    >
                        <FormControlLabel
                            sx={{ flex: '1 1 200px', m: 0, alignItems: 'center' }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={attachScreenshot}
                                    disabled={busy}
                                    onChange={(e) => setAttachScreenshot(e.target.checked)}
                                    data-testid="help-chat-screenshot"
                                />
                            }
                            label={
                                <Typography variant="caption" color="text.secondary" component="span">
                                    {t('navigator.help_chat_include_screenshot')}
                                </Typography>
                            }
                        />
                        <Button
                            variant="contained"
                            sx={{ flexShrink: 0 }}
                            endIcon={busy ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                            disabled={busy || !draft.trim()}
                            onClick={() => void send()}
                            data-testid="help-chat-send"
                        >
                            {t('navigator.help_chat_send')}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Popover>
    );
};

export default DashboardHelpChatDialog;
